import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getAllJobsForAdmin, updateJobStatus, activateJob, deactivateJob, approveJob, rejectJob, getJobStatistics, aiModerateJobLLM } from '@/services/jobService';
import { getAllCompaniesForAdmin } from '@/services/companyService';
import { JobListSkeleton } from '@/components/common/JobListSkeleton';
import JobDetailModal from '@/components/jobs/JobDetailModal';
import { JobRejectionDialog } from '@/components/jobs/JobRejectionDialog';
import { Pagination } from '@/components/common/Pagination';
import {
  Search,
  Briefcase,
  MapPin,
  Building2,
  Users,
  Calendar,
  DollarSign,
  Eye,
  Check,
  X,
  ChevronsUpDown,
  Clock,
  AlertCircle,
  Ban,
  Sparkles,
  XCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import * as adminService from '@/services/adminService';

export function JobManagement() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(''); // Input value
  const [searchTerm, setSearchTerm] = useState(''); // Actual search term for API
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [sortFilter, setSortFilter] = useState('createdAt_desc');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companySearchOpen, setCompanySearchOpen] = useState(false);
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyMeta, setCompanyMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 20
  });
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyLoadingMore, setCompanyLoadingMore] = useState(false);
  const companyRequestIdRef = useRef(0);
  const [stats, setStats] = useState({
    active: 0,
    pending: 0,
    expired: 0,
    inactive: 0,
    rejected: 0,
    total: 0
  });

  const [meta, setMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });

  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingJobId, setRejectingJobId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [recentActions, setRecentActions] = useState({}); // { jobId: 'APPROVED' | 'REJECTED' }

  const triggerVisualFeedback = (jobId, status) => {
    setRecentActions(prev => ({ ...prev, [jobId]: status }));
    // Clear the effect after 2 seconds
    setTimeout(() => {
      setRecentActions(prev => {
        const newState = { ...prev };
        delete newState[jobId];
        return newState;
      });
    }, 2000);
  };

  const fetchCompanies = useCallback(async ({ page = 1, search = '', append = false } = {}) => {
    const requestId = ++companyRequestIdRef.current;

    if (append) {
      setCompanyLoadingMore(true);
    } else {
      setCompanyLoading(true);
    }

    try {
      const response = await getAllCompaniesForAdmin({
        page,
        limit: companyMeta.limit,
        sort: 'name_asc',
        status: 'approved',
        search: search.trim() || undefined
      });

      if (requestId !== companyRequestIdRef.current) return;

      if (response.data?.success) {
        const companyList = response.data.data
          .filter((item) => item.company && item.company.name)
          .map((item) => ({
            id: item._id,
            name: item.company.name
          }));

        setCompanies((prev) => {
          if (!append) return companyList;
          const existingIds = new Set(prev.map((company) => company.id));
          const newItems = companyList.filter((company) => !existingIds.has(company.id));
          return [...prev, ...newItems];
        });

        if (response.data?.meta) {
          setCompanyMeta(response.data.meta);
        }
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      if (requestId === companyRequestIdRef.current) {
        setCompanyLoading(false);
        setCompanyLoadingMore(false);
      }
    }
  }, [companyMeta.limit]);

  useEffect(() => {
    if (!companySearchOpen) return;

    const debounceId = setTimeout(() => {
      setCompanyQuery(companySearchInput.trim());
    }, 300);

    return () => clearTimeout(debounceId);
  }, [companySearchInput, companySearchOpen]);

  useEffect(() => {
    if (!companySearchOpen) return;
    fetchCompanies({ page: 1, search: companyQuery, append: false });
  }, [companyQuery, companySearchOpen, fetchCompanies]);

  useEffect(() => {
    if (!companySearchOpen) {
      setCompanySearchInput('');
      setCompanyQuery('');
    }
  }, [companySearchOpen]);

  const hasMoreCompanies = companyMeta.currentPage < companyMeta.totalPages;

  const handleCompanyListScroll = (event) => {
    if (companyLoading || companyLoadingMore || !hasMoreCompanies) return;

    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 24) {
      fetchCompanies({
        page: companyMeta.currentPage + 1,
        search: companyQuery,
        append: true
      });
    }
  };

  // Fetch job stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await getJobStatistics();
      if (response.data?.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch job stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch jobs from API
  const fetchJobs = useCallback(async (page = meta.currentPage) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = {
        page: page,
        limit: meta.limit
      };

      if (searchTerm) queryParams.search = searchTerm;
      if (statusFilter !== 'all') queryParams.status = statusFilter;
      if (companyFilter) queryParams.company = companyFilter;
      if (sortFilter) queryParams.sort = sortFilter;

      const response = await getAllJobsForAdmin(queryParams);
      setJobs(response.data.data || []);
      setMeta(response.data?.meta || meta);
    } catch (error) {
      setError(error.message || 'Không thể tải danh sách công việc');
      toast.error(error.message || 'Không thể tải danh sách công việc');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, companyFilter, sortFilter, meta.limit]);

  const handlePageChange = (newPage) => {
    setMeta((prev) => ({ ...prev, currentPage: newPage }));
    fetchJobs(newPage);
  };

  // Load jobs when filters change - always reset to page 1
  useEffect(() => {
    setMeta((prev) => ({ ...prev, currentPage: 1 }));
    fetchJobs(1);
    fetchStats(); // Update stats when filters change or just on mount/action? Actually update on action is better, but here it keeps it fresh.
  }, [searchTerm, statusFilter, companyFilter, sortFilter]);

  // Handle search button click
  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };


  const handleStatusChange = useCallback(async (jobId, newStatus) => {
    try {
      setLoading(true);
      await updateJobStatus(jobId, newStatus);

      const statusMessages = {
        active: 'Đã phê duyệt công việc',
        inactive: 'Đã từ chối công việc',
        pending: 'Đã đưa công việc về trạng thái chờ duyệt'
      };
      toast.success(statusMessages[newStatus] || 'Đã cập nhật trạng thái công việc');

      // Refresh danh sách công việc từ server để cập nhật số liệu
      await fetchJobs();
      await fetchStats();
    } catch (error) {
      toast.error(error.message || 'Không thể cập nhật trạng thái công việc');
    } finally {
      setLoading(false);
    }
  }, [fetchJobs, fetchStats]);



  const handleDeactivateJob = useCallback(async (jobId) => {
    try {
      setLoading(true);
      await deactivateJob(jobId);

      // Update local state
      setJobs(prev => prev.map(job =>
        job._id === jobId ? { ...job, status: 'INACTIVE' } : job
      ));
      
      triggerVisualFeedback(jobId, 'REJECTED'); // Use red glow for deactivate
      fetchStats();

      toast.success('Đã vô hiệu hóa công việc');
    } catch (error) {
      toast.error(error.message || 'Không thể vô hiệu hóa công việc');
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  const handleApproveJob = useCallback(async (jobId) => {
    try {
      setLoading(true);
      await approveJob(jobId);

      // Update local state
      setJobs(prev => prev.map(job =>
        job._id === jobId ? { ...job, approved: true, moderationStatus: 'APPROVED', status: 'ACTIVE' } : job
      ));
      
      triggerVisualFeedback(jobId, 'APPROVED');
      fetchStats();

      toast.success('Đã phê duyệt công việc');
    } catch (error) {
      toast.error(error.message || 'Không thể phê duyệt công việc');
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  const handleRetryAI = useCallback(async (jobId) => {
    try {
      setLoading(true);
      const response = await aiModerateJobLLM(jobId);

      if (response.data?.success) {
        const { aiResult, job: updatedJob } = response.data.data;
        
        triggerVisualFeedback(jobId, aiResult.shouldApprove ? 'APPROVED' : 'REJECTED');
        
        // Remove or update from list
        setJobs(prev => prev.map(j => j._id === jobId ? { ...j, ...updatedJob } : j));
        fetchStats();

        if (aiResult.shouldApprove) {
          toast.success(`✅ AI đã phê duyệt thành công`);
        } else {
          toast.warning(`❌ AI đã từ chối job này`);
        }
      }
    } catch (error) {
      toast.error('AI vẫn đang gặp lỗi: ' + (error.message || 'Server error'));
      // Keep it in list, status remains PENDING with failed=true
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  const handleRejectClick = (jobId) => {
    setRejectingJobId(jobId);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectionReason.trim()) {
      toast.error('Vui lòng nhập lý do từ chối');
      return;
    }

    try {
      setLoading(true);
      await rejectJob(rejectingJobId, rejectionReason.trim());

      // Update local state
      setJobs(prev => prev.map(job =>
        job._id === rejectingJobId ? { ...job, approved: false, moderationStatus: 'REJECTED', status: 'INACTIVE' } : job
      ));

      triggerVisualFeedback(rejectingJobId, 'REJECTED');
      setRejectDialogOpen(false);
      setRejectingJobId(null);
      setRejectionReason('');
      fetchStats();

      toast.success('Đã từ chối công việc');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể từ chối công việc');
    } finally {
      setLoading(false);
    }
  }, [rejectingJobId, rejectionReason, fetchStats]);

  const handleRejectJob = useCallback(async (jobId) => {
    // Open dialog instead of direct rejection
    handleRejectClick(jobId);
  }, []);



  const handleViewJob = (jobId) => {
    setSelectedJobId(jobId);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedJobId(null);
  };

  const getStatusBadge = (job) => {
    const { status, moderationStatus } = job;

    if (moderationStatus === 'PENDING') {
      if (job.aiModerationResult?.failed || job.aiModerationResult?.prediction === 2) {
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">Chờ duyệt (Lỗi AI)</Badge>;
      }
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Chờ duyệt</Badge>;
    }
    if (moderationStatus === 'REJECTED') {
      return <Badge variant="destructive">Đã từ chối</Badge>;
    }

    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Đang tuyển</Badge>;
      case 'INACTIVE':
        return <Badge variant="secondary">Ngừng tuyển dụng</Badge>;
      case 'EXPIRED':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Hết hạn</Badge>;
      default:
        return <Badge variant="outline">Không xác định</Badge>;
    }
  };


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Quản lý Công việc</h1>
        <p className="text-gray-600">Giám sát và kiểm duyệt các tin tuyển dụng</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <span className="text-blue-600 font-bold text-2xl">{stats.total}</span>
            <span className="text-blue-800 text-sm font-medium mt-1">Tổng công việc</span>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <span className="text-green-600 font-bold text-2xl">{stats.active}</span>
            <span className="text-green-800 text-sm font-medium mt-1">Đang tuyển</span>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <span className="text-yellow-600 font-bold text-2xl">{stats.pending}</span>
            <span className="text-yellow-800 text-sm font-medium mt-1">Chờ duyệt</span>
          </CardContent>
        </Card>
        <Card className="bg-gray-100 border-gray-200">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <span className="text-gray-600 font-bold text-2xl">{stats.inactive}</span>
            <span className="text-gray-800 text-sm font-medium mt-1">Ngừng tuyển</span>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <span className="text-red-600 font-bold text-2xl">{stats.expired}</span>
            <span className="text-red-800 text-sm font-medium mt-1">Hết hạn</span>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Danh sách Công việc</CardTitle>
          <CardDescription>
            Xem xét và quản lý tất cả các tin tuyển dụng trên nền tảng
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Tìm kiếm theo tiêu đề..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="pl-10 pr-20"
              />
              <div className="absolute right-2 top-2 flex gap-1">
                {searchInput && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleClearSearch}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSearch}
                  disabled={loading}
                  className="h-6 px-2 text-xs"
                >
                  Tìm
                </Button>
              </div>
            </div>
            <Popover open={companySearchOpen} onOpenChange={setCompanySearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={companySearchOpen}
                  className="w-full justify-between"
                >
                  {companyFilter
                    ? companies.find((company) => company.id === companyFilter)?.name
                    : "Lọc theo công ty..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Tìm công ty..."
                    value={companySearchInput}
                    onValueChange={setCompanySearchInput}
                  />
                  <CommandList onScroll={handleCompanyListScroll}>
                    {!companyLoading && <CommandEmpty>Không tìm thấy công ty.</CommandEmpty>}
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setCompanyFilter('');
                          setCompanySearchOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            companyFilter === '' ? "opacity-100" : "opacity-0"
                          )}
                        />
                        Tất cả công ty
                      </CommandItem>
                      {companies.map((company) => (
                        <CommandItem
                          key={company.id}
                          value={`${company.id}-${company.name}`}
                          keywords={[company.name]}
                          onSelect={() => {
                            setCompanyFilter(company.id);
                            setCompanySearchOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              companyFilter === company.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {company.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {companyLoading && (
                      <div className="px-3 py-2 text-sm text-gray-500">Đang tải công ty...</div>
                    )}
                    {companyLoadingMore && (
                      <div className="px-3 py-2 text-sm text-gray-500">Đang tải thêm...</div>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                <SelectItem value="PENDING">Chờ duyệt</SelectItem>
                <SelectItem value="AI_FAILED">Lỗi duyệt tự động (Cần duyệt tay)</SelectItem>
                <SelectItem value="ACTIVE">Đang tuyển</SelectItem>
                <SelectItem value="INACTIVE">Ngừng tuyển dụng</SelectItem>
                <SelectItem value="EXPIRED">Hết hạn ứng tuyển</SelectItem>
                <SelectItem value="REJECTED">Đã từ chối</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortFilter} onValueChange={setSortFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Sắp xếp theo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt_desc">Mới nhất</SelectItem>
                <SelectItem value="createdAt_asc">Cũ nhất</SelectItem>
                <SelectItem value="title_desc">Tiêu đề (Z-A)</SelectItem>
                <SelectItem value="title_asc">Tiêu đề (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <JobListSkeleton />
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-700 mb-2">{error}</div>
              <Button onClick={() => fetchJobs()} variant="outline">
                Thử lại
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job) => (
                <Card 
                  key={job._id} 
                  className={cn(
                    "border border-gray-200 hover:shadow-md transition-all duration-300",
                    recentActions[job._id] === 'APPROVED' && "animate-moderate-approve ring-2 ring-green-500",
                    recentActions[job._id] === 'REJECTED' && "animate-moderate-reject ring-2 ring-red-500"
                  )}
                >
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between gap-10">
                      <div className="flex items-start gap-6 flex-1">
                        <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {job.recruiterProfileId?.company?.logo ? (
                            <img
                              src={job.recruiterProfileId.company.logo}
                              alt={job.recruiterProfileId?.company?.name || 'Company Logo'}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <Building2 className="w-8 h-8 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold">{job.title}</h3>
                            {getStatusBadge(job)}
                          </div>
                          <div className="space-y-3 mb-4">
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              <Building2 className="w-4 h-4 flex-shrink-0" />
                              <span>{job.recruiterProfileId?.company?.name || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400 mt-4">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            <span>Đăng ngày {new Date(job.createdAt).toLocaleDateString('vi-VN')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewJob(job._id)}
                          className="whitespace-nowrap px-4 py-2"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Xem
                        </Button>
                        {job.moderationStatus === 'PENDING' && (
                          <>
                            {(job.aiModerationResult?.failed || job.aiModerationResult?.prediction === 2) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRetryAI(job._id)}
                                disabled={loading}
                                className="whitespace-nowrap px-4 py-2 border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
                              >
                                <Sparkles className="w-4 h-4 mr-2" />
                                Thử lại AI
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectJob(job._id)}
                              disabled={loading}
                              className="whitespace-nowrap px-4 py-2"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Từ chối
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleApproveJob(job._id)}
                              className="bg-green-600 hover:bg-green-700 whitespace-nowrap px-4 py-2"
                              disabled={loading}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Phê duyệt
                            </Button>
                          </>
                        )}
                        {job.moderationStatus === 'REJECTED' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 whitespace-nowrap px-4 py-2"
                            onClick={() => handleApproveJob(job._id)}
                            disabled={loading}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Duyệt lại
                          </Button>
                        )}


                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && jobs.length === 0 && (
            <div className="text-center py-8">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Không tìm thấy công việc nào phù hợp với tiêu chí.</p>
            </div>
          )}
          <div className="mt-6">
            <Pagination
              currentPage={meta.currentPage}
              totalPages={meta.totalPages}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Job Detail Modal */}
      <JobDetailModal
        jobId={selectedJobId}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
      />

      {/* Rejection Reason Dialog */}
      <JobRejectionDialog
        isOpen={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        rejectionReason={rejectionReason}
        onReasonChange={setRejectionReason}
        onConfirm={handleRejectConfirm}
        onCancel={() => {
          setRejectDialogOpen(false);
          setRejectingJobId(null);
          setRejectionReason('');
        }}
        loading={loading}
      />
    </div>
  );
}
